import React, { useEffect, lazy } from 'react';
import {
  createBrowserRouter,
  RouterProvider,
  useLocation,
  Navigate,
} from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { ErrorBoundary } from 'react-error-boundary';
import { MainLayout } from './components/layout';
// HomePage loads eagerly - it's the first thing almost every visitor sees,
// so lazy-loading it would only add a chunk-fetch delay with no benefit.
import { HomePage } from './pages/Home';
import { AuthProvider, useAuth } from './context/AuthContext';

// Every other route is code-split so the initial bundle only ships what a
// first-time visitor to "/" actually needs - Dashboard in particular pulls
// in a large admin form and shouldn't block a regular user's first paint.
const EventsPage = lazy(() => import('./pages/Events').then((m) => ({ default: m.EventsPage })));
const EventDetailPage = lazy(() => import('./pages/EventDetail').then((m) => ({ default: m.EventDetailPage })));
const CheckoutPage = lazy(() => import('./pages/Checkout').then((m) => ({ default: m.CheckoutPage })));
const MyTicketsPage = lazy(() => import('./pages/MyTickets').then((m) => ({ default: m.MyTicketsPage })));
const AboutPage = lazy(() => import('./pages/About').then((m) => ({ default: m.AboutPage })));
const UpcomingPage = lazy(() => import('./pages/Upcoming').then((m) => ({ default: m.UpcomingPage })));
const PartnersPage = lazy(() => import('./pages/Partners').then((m) => ({ default: m.PartnersPage })));
const AuthPage = lazy(() => import('./pages/Auth').then((m) => ({ default: m.AuthPage })));
const DashboardPage = lazy(() => import('./pages/Dashboard').then((m) => ({ default: m.DashboardPage })));
const ProfilePage = lazy(() => import('./pages/Profile').then((m) => ({ default: m.ProfilePage })));
const PrivacyPage = lazy(() => import('./pages/Static').then((m) => ({ default: m.PrivacyPage })));
const TermsPage = lazy(() => import('./pages/Static').then((m) => ({ default: m.TermsPage })));
const PaymentStatusPage = lazy(() => import('./pages/Payment').then((m) => ({ default: m.PaymentStatusPage })));

const ProtectedRoute = ({ children, requireAdmin = false }: { children: React.ReactNode, requireAdmin?: boolean }) => {
  const { isLoggedIn, user } = useAuth();
  const location = useLocation();

  if (!isLoggedIn) {
    return <Navigate to="/auth" replace state={{ from: { pathname: location.pathname } }} />;
  }

  if (requireAdmin && user?.role !== 'admin') {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false, // Turn off by default for a ticketing app
      retry: 1, // Only retry once
      // Events/catalog data doesn't change second-to-second - treat it as
      // fresh for a minute so Navbar/Home/Events mounting independently
      // (all under the same ['events'] query key) don't each trigger a
      // redundant background refetch on every navigation.
      staleTime: 60_000,
    },
  },
});

const ScrollToTop = () => {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    // When navigating to an in-page anchor (e.g. /#home-cities), let that page's
    // own smooth-scroll land on the section instead of first jumping to the top -
    // otherwise the two motions fight and the landing looks broken.
    if (hash) return;
    window.scrollTo(0, 0);
  }, [pathname, hash]);

  return null;
};

// Fallback UI for catastrophic crashes
const GlobalErrorFallback = ({ error, resetErrorBoundary }: { error: any, resetErrorBoundary: () => void }) => {
  return (
    <div className="min-h-screen bg-[#0b0b0f] flex flex-col items-center justify-center p-6 text-center font-sans">
      <div className="glass-panel p-8 rounded-3xl max-w-md w-full animate-fade-in-up">
        <div className="text-6xl mb-6 opacity-80">🚨</div>
        <h2 className="text-h3 text-white mb-2 uppercase tracking-wide">Something went wrong</h2>
        <p className="text-white/50 text-sm font-light mb-6">
          Our systems encountered an unexpected error. Don't worry, your data is safe.
        </p>
        <div className="glass-panel-subtle rounded-xl p-4 mb-8 text-left overflow-auto max-h-32">
          <code className="text-xs text-red-400 font-mono break-all">{error.message}</code>
        </div>
        <button
          onClick={resetErrorBoundary}
          className="w-full bg-primary hover:bg-primary/80 text-white font-medium tracking-widest uppercase text-sm py-4 rounded-xl transition-all shadow-[0_0_20px_rgba(229,57,53,0.3)]"
        >
          Try Again
        </button>
      </div>
    </div>
  );
};

const RootLayout = () => {
  return (
    <>
      <ScrollToTop />
      <MainLayout />
    </>
  );
};

const router = createBrowserRouter([
  {
    path: "/",
    element: <RootLayout />,
    children: [
      { index: true, element: <HomePage /> },
      { path: "events", element: <EventsPage /> },
      { path: "events/:slug", element: <EventDetailPage /> },
      { path: "checkout", element: <CheckoutPage /> },
      { path: "payment/success", element: <PaymentStatusPage /> },
      { path: "payment/failure", element: <PaymentStatusPage /> },
      { path: "my-tickets", element: <ProtectedRoute><MyTicketsPage /></ProtectedRoute> },
      { path: "about", element: <AboutPage /> },
      { path: "upcoming", element: <UpcomingPage /> },
      { path: "partners", element: <PartnersPage /> },
      { path: "contact", element: <PartnersPage /> },
      { path: "auth", element: <AuthPage /> },
      { path: "dashboard", element: <ProtectedRoute requireAdmin><DashboardPage /></ProtectedRoute> },
      { path: "profile", element: <ProtectedRoute><ProfilePage /></ProtectedRoute> },
      { path: "privacy", element: <PrivacyPage /> },
      { path: "terms", element: <TermsPage /> },
    ]
  }
]);

const App: React.FC = () => {
  return (
    <ErrorBoundary FallbackComponent={GlobalErrorFallback}>
      <QueryClientProvider client={queryClient}>
        <Toaster
          position="top-center"
          toastOptions={{
            style: {
              background: '#1a1a1a',
              color: '#fff',
              border: '1px solid rgba(255,255,255,0.1)',
              backdropFilter: 'blur(10px)',
            },
            success: {
              iconTheme: { primary: '#4caf50', secondary: '#1a1a1a' },
            },
            error: {
              iconTheme: { primary: '#e53935', secondary: '#1a1a1a' },
            },
          }}
        />
        <AuthProvider>
          <RouterProvider router={router} />
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};
 
export default App;